import type {Behavior, Segmentation} from 'common/types/reports/query';
import type {
  SerializedBehaviorDictionary,
  User,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import type {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import {PropertyType, ResourceType} from 'common/report/constants';
/* eslint-disable-next-line import/no-cycle */
import type {QueryCohortEntry} from 'common/query-builder/query-entry-section/types';

/*
  TODO: CohortsSegments should reference the serialized definitions in /serialized-cohort-types.ts
  > export type SavedCohortSegment = {[ResourceType.Cohort]: SerializedSavedCohort}
  > export type InlineCohortSegment = {[ResourceType.Cohort]: SerializedInlineCohort}
  This is dependent on new data format refactors in the serialized definitions
*/
export interface SavedCohortSegment {
  [ResourceType.Cohort]: {
    id: number;
    name?: string;
    negated: boolean;
    data_group_id?: string;
  };
}

export interface InlineCohortSegment {
  [ResourceType.Cohort]: {
    name?: string;
    raw_cohort: {
      name: string;
      behaviors: SerializedBehaviorDictionary;
      selector: Expression;
      data_group_id?: string;
      unsavedId: string;
      createdBy: User;
    };
    negated?: boolean;
  };
}

/** Funnels & Flows define this type of segmentation */
export type OtherSegmentation = Segmentation & {value: string; label?: string};
export interface OtherSegment {
  [ResourceType.Other]: OtherSegmentation;
}

/** A single segment for breaking down a query */
export type SegmentType = EventSegment | UserSegment | OtherSegment;
export type Segment = SegmentType | SegmentType[] | CohortSegment[];

/**
 * The `Segmentation` type defines a property selection with all of its associated fields: look up tables, custom properties, behaviors, etc...
 * however, a persisted segment from the UI is stored in a different way across our APIs; for Flows, Funnels & Retention it is
 * defined as `{[key: ResourceType]: Segmentation | Cohort}`, this is what the `Segment` type defines, for Insights this is defined in a different
 * way so we must define a single type for rendering segments in the UI, in the past this has resulted in complicated types and ways of defining multi-seg
 * representations so we simplify that by flattening segments and multi-cohort segmentation is represented as an array.
 *
 * This type will also fit for doing value comparisons in which one segment group is defined by multiple Segmentation entries
 */
export type SegmentValue = Segmentation | CohortSegment[] | Segmentation[];

export function isSegmentation(
  segmentValue: SegmentValue | Segmentation | Behavior | null,
): segmentValue is Segmentation {
  if (!segmentValue) {
    return false;
  }
  return Boolean((segmentValue as Segmentation).propertyName);
}

export function isListOfObjectsSegment(segment?: Segmentation) {
  return segment?.joinPropertyType === PropertyType.List && segment?.propertyType === PropertyType.Object;
}

export function isNonEmptySegmentSelection(selection: Segment) {
  return Array.isArray(selection) && selection.length !== 0;
}

export function isCohortSegment(segment: Segment | Segmentation | Segmentation[]): segment is CohortSegment[] {
  return Array.isArray(segment) && !!Object.prototype.hasOwnProperty.call(segment[0] ?? {}, ResourceType.Cohort);
}
export type CohortSegment = SavedCohortSegment | InlineCohortSegment;

export function isCohortSegmentItem(item: QueryCohortEntry | CohortSegment): item is CohortSegment {
  return Object.prototype.hasOwnProperty.call(item, ResourceType.Cohort);
}

export function isSavedCohortSegment(segment: CohortSegment): segment is SavedCohortSegment {
  return Number.isInteger((segment as SavedCohortSegment).cohort.id);
}

export function isInlineCohortSegment(segment: CohortSegment): segment is InlineCohortSegment {
  return !!(segment as InlineCohortSegment).cohort.raw_cohort;
}

export function isUserSegment(segment: Segment): segment is UserSegment {
  return [].concat(segment).some((seg) => Object.prototype.hasOwnProperty.call(seg, ResourceType.User));
}
export interface UserSegment {
  [ResourceType.User]: Segmentation;
}

export function isEventSegment(segment: Segment): segment is EventSegment {
  return [].concat(segment).some((seg) => Object.prototype.hasOwnProperty.call(seg, ResourceType.Event));
}
export interface EventSegment {
  [ResourceType.Event]: Segmentation;
}

export function isOtherSegment(segment: Segment): segment is OtherSegment {
  return Object.prototype.hasOwnProperty.call(segment, ResourceType.Other);
}

export function getFirstSegment(segment: Segment): Segmentation | OtherSegmentation {
  // The below assertion doesn't make sense because we have a valid code path which passes in cohort segment (E.g.
  // retention. TODO [jun] to figure out what this assertion is supposed to do.
  // assert(!isCohortSegment(segment), `unexpected`);
  const firstSegment: Segment = [].concat(segment)[0];
  return Object.values(firstSegment)[0];
}

export function isNumericSegment(segment: Segment): segment is EventSegment | UserSegment {
  if (isEventSegment(segment) || isUserSegment(segment)) {
    return getFirstSegment(segment).propertyType === PropertyType.Number;
  }
  return false;
}

export function isDateSegment(segment: Segment): segment is EventSegment | UserSegment {
  if (isEventSegment(segment) || isUserSegment(segment)) {
    return getFirstSegment(segment).propertyType === PropertyType.Datetime;
  }
  return false;
}

export function getSegmentPropertyType(segment: Segment) {
  if (isCohortSegment(segment)) {
    // default for cohort
    return PropertyType.String;
  }
  return getFirstSegment(segment).propertyType;
}

/**
 * Infers selection type from the first selected segment
 */
export function getSegmentType(selection: Segment): ResourceType | null {
  if (!selection) {
    return null;
  }

  if (isCohortSegment(selection)) {
    return ResourceType.Cohort;
  }

  // select first item for a value comparison
  if (Array.isArray(selection)) {
    selection = selection[0];
  }

  return Object.values(ResourceType).find((resourceType) => resourceType in selection) || null;
}

export function getSegmentValueResourceType(selection: SegmentValue): ResourceType | null {
  if (!selection || (Array.isArray(selection) && selection.length === 0)) {
    return null;
  }

  if (isCohortSegment(selection)) {
    return ResourceType.Cohort;
  }

  const firstSegment: Segmentation = [].concat(selection)[0];
  return firstSegment.resourceType;
}

export function getSegmentValuePropertyType(selection: SegmentValue): PropertyType | null {
  if (!selection || (Array.isArray(selection) && selection.length === 0)) {
    return null;
  }

  const firstSegment: Segmentation = [].concat(selection)[0];

  if (
    isCohortSegment(selection) || firstSegment.joinPropertyType
      ? firstSegment.joinPropertyType === PropertyType.List
      : false
  ) {
    return PropertyType.List;
  }

  return firstSegment.propertyType;
}

export function getFirstSegmentValue(selection: SegmentValue): Segmentation | null {
  if (!selection || (Array.isArray(selection) && selection.length === 0)) {
    return null;
  }

  const firstSegment: Segmentation = [].concat(selection)[0];
  return firstSegment;
}
